import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { listPostmarkIdentities } from './api';
import {
  NEWSLETTER_SENDER_FROM,
  NEWSLETTER_SENDER_REPLY_TO,
} from '../../lib/newsletterSender';
import type { PostmarkIdentity } from './types';

const fieldLabel =
  'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2';
const fieldInput =
  'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500';

export interface PostmarkSignatureSelectProps {
  value: number | null;
  onChange: (signatureId: number | null) => void;
  disabled?: boolean;
}

function EnvelopePreview({
  from,
  replyTo,
}: {
  from: string;
  replyTo: string;
}) {
  return (
    <div className="mt-2 space-y-1 text-xs text-gray-700 dark:text-gray-300">
      <p>
        <span className="text-gray-500 dark:text-gray-400">From:</span>{' '}
        <span className="font-mono break-all">{from}</span>
      </p>
      <p>
        <span className="text-gray-500 dark:text-gray-400">Reply-To:</span>{' '}
        <span className="font-mono break-all">{replyTo}</span>
      </p>
    </div>
  );
}

export default function PostmarkSignatureSelect({
  value,
  onChange,
  disabled = false,
}: PostmarkSignatureSelectProps) {
  const [signatures, setSignatures] = useState<PostmarkIdentity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listPostmarkIdentities();
      setSignatures(result.identities.filter((row) => row.kind === 'signature'));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load signatures';
      setError(msg);
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => signatures.find((s) => s.id === value) ?? null,
    [signatures, value],
  );

  return (
    <div>
      <label className={fieldLabel} htmlFor="postmark-signature-select">
        Postmark signature
      </label>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
          Loading signatures…
        </div>
      ) : (
        <select
          id="postmark-signature-select"
          className={fieldInput}
          disabled={disabled}
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === '' ? null : Number(raw));
          }}
        >
          <option value="">Default (engine — {NEWSLETTER_SENDER_FROM})</option>
          {signatures.map((sig) => (
            <option key={sig.id} value={sig.id}>
              {sig.name ? `${sig.name} · ` : ''}
              {sig.emailAddress ?? sig.fromLine} (id {sig.id})
            </option>
          ))}
        </select>
      )}
      {error ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : null}
      {selected ? (
        <EnvelopePreview
          from={selected.fromLine}
          replyTo={selected.replyToEmailAddress ?? 'not set'}
        />
      ) : (
        <EnvelopePreview from={NEWSLETTER_SENDER_FROM} replyTo={NEWSLETTER_SENDER_REPLY_TO} />
      )}
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        Resolved from Postmark at send time. Create signatures in Postmark for each brand
        (LCCTO, Pickles, Fractional, etc.). Domains alone are not selectable here — add a
        signature row per From address.
      </p>
    </div>
  );
}
